import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, SendHorizontal, X } from 'lucide-react';

const MAX_BODY_CHARS = 4000;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
// Alineado con el backend (que a su vez se alinea con save_private_document):
// sin gif — el almacén privado no admite esa extensión.
const MIMES_ADMITIDOS = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

/**
 * Cajetín de envío: texto + clip de adjunto (imagen/PDF ≤10 MB, la misma
 * allow-list que exige el API). Enter envía; Shift+Enter hace salto de línea.
 */
export default function MessageComposer({ onEnviar, enviando }) {
  const [texto, setTexto] = useState('');
  const [fichero, setFichero] = useState(null);
  const [aviso, setAviso] = useState('');
  const fileInputRef = useRef(null);

  // Miniatura del adjunto ANTES de enviar (pedida en el smoke del 22-ago):
  // object URL ligado al fichero, revocado al cambiarlo o desmontar.
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!fichero || !fichero.type.startsWith('image/')) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(fichero);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [fichero]);

  const elegirFichero = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!MIMES_ADMITIDOS.includes(f.type)) {
      setAviso('Solo imágenes o PDF.');
      return;
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      setAviso('El adjunto supera los 10 MB.');
      return;
    }
    setAviso('');
    setFichero(f);
  };

  const enviar = async () => {
    const body = texto.trim();
    if ((!body && !fichero) || enviando) return;
    if (body.length > MAX_BODY_CHARS) {
      setAviso(`Máximo ${MAX_BODY_CHARS} caracteres.`);
      return;
    }
    const ok = await onEnviar(body, fichero);
    if (ok) {
      setTexto('');
      setFichero(null);
      setAviso('');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <div className="border-t border-gray-200 p-3">
      {aviso ? <p className="pb-1.5 text-xs text-red-600">{aviso}</p> : null}
      {fichero ? (
        <div className="mb-1.5">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`Adjunto: ${fichero.name}`}
              className="mb-1.5 max-h-28 rounded-lg border border-gray-200 object-contain"
            />
          ) : null}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
            📎 {fichero.name}
            <button
              type="button"
              onClick={() => setFichero(null)}
              className="rounded-full p-0.5 hover:bg-gray-300"
              aria-label="Quitar adjunto"
            >
              <X size={12} />
            </button>
          </span>
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={MIMES_ADMITIDOS.join(',')}
          className="hidden"
          onChange={elegirFichero}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Adjuntar fichero"
        >
          <Paperclip size={18} />
        </button>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Escribe un mensaje…"
          className="max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#FF5400] focus:outline-none focus:ring-1 focus:ring-[rgba(255,84,0,0.15)]"
        />
        {/* #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera de tokens.css a propósito: el design system es slate y este naranja es exclusivo del módulo. */}
        <button
          type="button"
          onClick={enviar}
          disabled={enviando || (!texto.trim() && !fichero)}
          className="shrink-0 rounded-lg bg-[#FF5400] p-2 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300"
          aria-label="Enviar mensaje"
        >
          <SendHorizontal size={18} />
        </button>
      </div>
    </div>
  );
}
